import React, { useEffect, useRef, useState } from 'react'

export interface TopScrollSyncProps {
  targetRef: React.RefObject<HTMLElement | null>
  className?: string
}

export function TopScrollSync({ targetRef, className = '' }: TopScrollSyncProps): React.ReactElement {
  const topScrollRef = useRef<HTMLDivElement>(null)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [clientWidth, setClientWidth] = useState(0)

  useEffect(() => {
    const target = targetRef.current
    const top = topScrollRef.current
    if (!target) return

    const updateDimensions = () => {
      if (target) {
        setScrollWidth(target.scrollWidth)
        setClientWidth(target.clientWidth)
      }
    }

    updateDimensions()

    let isSyncing = false

    const handleTargetScroll = () => {
      if (isSyncing || !top) return
      isSyncing = true
      top.scrollLeft = target.scrollLeft
      requestAnimationFrame(() => {
        isSyncing = false
      })
    }

    const handleTopScroll = () => {
      if (isSyncing || !target || !top) return
      isSyncing = true
      target.scrollLeft = top.scrollLeft
      requestAnimationFrame(() => {
        isSyncing = false
      })
    }

    target.addEventListener('scroll', handleTargetScroll, { passive: true })
    if (top) {
      top.addEventListener('scroll', handleTopScroll, { passive: true })
    }

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions()
      })
      resizeObserver.observe(target)
    } else {
      window.addEventListener('resize', updateDimensions)
    }

    return () => {
      target.removeEventListener('scroll', handleTargetScroll)
      if (top) {
        top.removeEventListener('scroll', handleTopScroll)
      }
      if (resizeObserver) {
        resizeObserver.disconnect()
      } else {
        window.removeEventListener('resize', updateDimensions)
      }
    }
  }, [targetRef])

  const hasOverflow = scrollWidth > clientWidth

  return (
    <div
      ref={topScrollRef}
      data-testid="top-scroll-sync"
      className={`overflow-x-auto overflow-y-hidden bg-slate-100/70 border-b border-slate-200 transition-all ${
        hasOverflow ? 'h-2.5 opacity-100' : 'h-0 opacity-0 pointer-events-none'
      } ${className}`.trim()}
      aria-hidden="true"
    >
      <div style={{ width: `${Math.max(scrollWidth, 1)}px`, height: '1px' }} />
    </div>
  )
}
